/**
 * Narrow hook for resolving CRM entity-type labels.
 *
 * This is the label-only counterpart to `useSchemaLabels`. It fetches ONLY the
 * org's object-type schema — the same `entityTypesQueryKey` payload already
 * warmed on virtually every page load — and nothing else.
 *
 * Prefer this hook whenever a component needs `getEntityTypeLabel` and nothing
 * more. `useSchemaLabels` additionally issues an unfiltered
 * `/schemas/property-types/` request (the ENTIRE org schema — measured at
 * 364 KB / 690 property types in a production HAR) plus `useStages`, which
 * fans out one `crm-objects/query` per discovered stage type (6 parallel
 * requests in the same capture). Callers that only render a type label pay all
 * of that for data they never read; on the record timeline it gave back ~88% of
 * the win from making association fetching opt-in.
 *
 * Reach for `useSchemaLabels` only when you genuinely need stage labels,
 * property-option labels, or the inference-based `resolveLabel`.
 */

import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { CrmObjectTypeSchema } from '../core/SchemaClient';
import { getTypeLabelByName } from '../utils/getTypeLabel';
import {
  entityTypesQueryKey,
  fetchEntityTypeSchemas,
} from './useEntityTypeSchema';

/**
 * Hoisted empty default so `entityTypes` keeps a stable identity while the
 * query is pending. An inline `= []` default mints a new array on every render,
 * which would change the `getEntityTypeLabel` callback identity every render
 * and defeat memoization in every consumer.
 */
const NO_ENTITY_TYPES: CrmObjectTypeSchema[] = [];

export interface UseEntityTypeLabelResult {
  /**
   * Get a human-readable label for a CRM entity type name.
   * Falls back through schema → built-in registry → snake-case formatting.
   */
  getEntityTypeLabel: (typeName: string) => string;

  /** The raw object-type schema list backing the lookup. */
  entityTypes: CrmObjectTypeSchema[];

  /** Loading state of the object-type schema query. */
  isLoading: boolean;

  /** Error from the object-type schema query, if any. */
  error: Error | null;
}

/**
 * Hook to resolve CRM entity-type labels from the object-type schema.
 *
 * @param orgId - Organization ID
 * @returns Label resolver, the backing schema list, and query state
 *
 * @example
 * const { getEntityTypeLabel } = useEntityTypeLabel(orgId);
 * getEntityTypeLabel('ahoy_contact'); // 'Contact'
 */
export function useEntityTypeLabel(
  orgId: number | undefined
): UseEntityTypeLabelResult {
  const {
    data: entityTypes = NO_ENTITY_TYPES,
    isLoading,
    error,
  } = useQuery({
    queryKey: entityTypesQueryKey(orgId),
    queryFn: () => fetchEntityTypeSchemas(orgId ?? 0),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000, // 5 minute cache — schema changes are rare
  });

  const getEntityTypeLabel = useCallback(
    (typeName: string): string => getTypeLabelByName(typeName, {}, entityTypes),
    [entityTypes]
  );

  return {
    getEntityTypeLabel,
    entityTypes,
    isLoading,
    error: (error as Error | null) ?? null,
  };
}
